import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { calenderCheck } from "../../Store/apiStore";
import decodeToken from "../../lib/decodeToken";
import {
  markSetupStep,
  fetchSetupScore,
  SETUP_STEP_DISPLAY,
} from "../../lib/setupScore";
import NewModal from "../NewModal/NewModal";
import styles from "./GoLiveCelebration.module.css";

/*
 * GoLiveCelebration — Screen 15 ("Sophia is Live").
 *
 * Final stop in the onboarding chain:
 *   GoLiveWizard → /contact-sync → /go-live-celebration → /fast-agent-detail
 *
 * Layout per Figma:
 *   - Headline + subhead with the business name + 24/7 callout
 *   - Sophia avatar (large)
 *   - Setup Summary card (5 rows, each with icon + green tick)
 *   - "Make a test call" prompt + tap-to-dial pill
 *   - Purple "Go to Dashboard →" CTA
 *   - Two-line bullet footer (Sophia protection note + trial-end notice)
 *
 * Behaviour:
 *   - On mount: 3-stage canvas-confetti burst (one centre cannon + two
 *     side cannons 250ms later). Tasteful, total runtime under 2s.
 *   - Tap-to-dial: native `tel:` link (works in browsers + RN WebView
 *     when the host allows the scheme). We also fire markSetupStep
 *     "firstRealCall" (5 pts) on tap because the user explicitly told
 *     us a tap counts. Server-side helper is idempotent so a real-call
 *     webhook firing it later is harmless.
 *   - Calendar row reads /calender-check once to flip the connected
 *     subtext. Other rows are static per the Figma — Spam / 24-7 are
 *     always-on baseline features at this point in onboarding.
 *
 * Data sources (all soft-defaults so a missing piece never blanks the
 * screen):
 *   - sessionStorage.agentDetails → { agent, business }
 *   - first item of agent.voip_numbers (handles JSON-string + array
 *     storage formats — same defensive parse used in
 *     ImmediateOwnerShip.js).
 *   - business.businessName / agent.agentName fall back to "Your
 *     business" / "Sophia".
 */

const VOIP_PARSE = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw[0] || null;
  if (typeof raw === "string") {
    if (raw.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed[0] || null : parsed;
      } catch {
        return raw;
      }
    }
    return raw;
  }
  return null;
};

const formatPhoneDisplay = (num) => {
  const cleaned = String(num || "").replace(/\D/g, "");
  if (cleaned.length < 10) return String(num || "");
  const last10 = cleaned.slice(-10);
  const cc = cleaned.length > 10 ? cleaned.slice(0, cleaned.length - 10) : "1";
  return `+${cc} ${last10.slice(0, 3)}-${last10.slice(3, 6)}-${last10.slice(6)}`;
};

const dialHref = (num) => {
  const cleaned = String(num || "").replace(/[^\d+]/g, "");
  if (!cleaned) return "tel:";
  return `tel:${cleaned.startsWith("+") ? cleaned : "+" + cleaned}`;
};

// ── Inline icons. Kept inline (not /svg/...) so the celebration screen
// renders correctly even if the public SVG bundle is slow on first
// paint after Go Live. Each icon picks up `currentColor` so colour is
// driven by the parent's CSS purple. ────────────────────────────────────
const PhoneIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 5 5L14 13l5 2v3a2 2 0 0 1-2 2A14 14 0 0 1 3 6a2 2 0 0 1 2-2z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ReceptionistIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 13a7 7 0 0 1 14 0v3a2 2 0 0 1-2 2h-1v-5h3M5 13v3a2 2 0 0 0 2 2h1v-5H5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="9" r="2.2" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3l8 3v6c0 4.5-3.4 8.5-8 9-4.6-.5-8-4.5-8-9V6l8-3z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const HeadphonesIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 14a8 8 0 1 1 16 0v3a3 3 0 0 1-3 3h-1v-6h4M4 14v3a3 3 0 0 0 3 3h1v-6H4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Small green check badge that overlaps the top-right of each icon
// circle — same visual language as Setup Summary tick rows.
const CheckBadge = () => (
  <span className={styles.checkBadge} aria-hidden="true">
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12.5l4.5 4.5L19 7"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Small star — used inside the Setup Score pill at the top of the
// celebration screen. Filled, brand purple. Calling it inline so the
// pill renders correctly even if the public SVG bundle hasn't loaded
// (same reason the row icons above are inline).
const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.5l2.95 6.31 6.95.6-5.27 4.6 1.59 6.79L12 17.27l-6.22 3.53 1.59-6.79-5.27-4.6 6.95-.6L12 2.5z" />
  </svg>
);

// Big tick used by the breakdown modal's "done" rows. Independent of
// CheckBadge above (which is a tiny corner badge) so each can be
// styled at its own scale.
const RowTick = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12.5l4.5 4.5L19 7"
      stroke="#ffffff"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/*
 * Score-breakdown modal. Renders all 8 setup-score steps in display
 * order from SETUP_STEP_DISPLAY, marking each as done (purple ✓
 * circle) or pending (grey ○ ring). Shows the running total and a
 * footer line nudging the user toward whatever is left.
 *
 * Receives the raw `score` shape returned by /api/onboarding/score
 * — `{ percent, steps: { stepKey: { done, at, points } } }`. We map
 * stepKey → display-order via SETUP_STEP_DISPLAY (defined in
 * setupScore.js so backend + this UI stay in lock-step).
 */
function ScoreBreakdownModal({ isOpen, onClose, score }) {
  const steps = score?.steps || {};
  const earned = Number(score?.percent || 0);
  const remaining = Math.max(0, 100 - earned);
  // Display total — sum from the catalogue so we always show "/ 100"
  // even if the API returns a truncated shape.
  const total = SETUP_STEP_DISPLAY.reduce((sum, s) => sum + s.points, 0);

  return (
    <NewModal
      isOpen={isOpen}
      onClose={onClose}
      title="Setup Score"
      height="auto"
      maxHeight="85vh"
    >
      <div className={styles.scoreModalHeader}>
        <span className={styles.scoreModalTotal}>
          <span className={styles.scoreModalTotalNum}>{earned}</span>
          <span className={styles.scoreModalTotalDenom}>/ {total} pts</span>
        </span>
        {/* Progress bar — visual reinforcement of the numeric total. */}
        <span
          className={styles.scoreModalBar}
          aria-hidden="true"
        >
          <span
            className={styles.scoreModalBarFill}
            style={{ width: `${Math.min(100, earned)}%` }}
          />
        </span>
      </div>

      <ul className={styles.scoreModalList}>
        {SETUP_STEP_DISPLAY.map((def) => {
          const done = Boolean(steps[def.key]?.done);
          return (
            <li
              key={def.key}
              className={`${styles.scoreModalRow} ${
                done ? styles.scoreModalRowDone : ""
              }`}
            >
              <span
                className={
                  done ? styles.scoreModalCheck : styles.scoreModalPending
                }
                aria-hidden="true"
              >
                {done ? <RowTick /> : null}
              </span>
              <span className={styles.scoreModalText}>
                <span className={styles.scoreModalLabel}>{def.label}</span>
                {!done && def.hint && (
                  <span className={styles.scoreModalHint}>{def.hint}</span>
                )}
              </span>
              <span
                className={
                  done
                    ? styles.scoreModalPointsDone
                    : styles.scoreModalPoints
                }
              >
                {def.points} pts
              </span>
            </li>
          );
        })}
      </ul>

      {/* <div className={styles.scoreModalFooter}>
        {remaining > 0
          ? `Earn ${remaining} more to fully unlock Sophia.`
          : "All setup steps complete — Sophia is fully unlocked. 🎉"}
      </div> */}
    </NewModal>
  );
}

export default function GoLiveCelebration() {
  const navigate = useNavigate();
  const confettiFiredRef = useRef(false);

  // ── Setup score (clickable pill + breakdown modal) ───────────
  // `score` is the raw shape from /api/onboarding/score — null
  // until the fetch resolves. We don't render the pill while it's
  // null so the user never sees a "0 / 100 pts" flash before the
  // real data lands. fetchSetupScore is fail-soft (returns null on
  // any error) so the celebration never breaks if the API is down.
  const [score, setScore] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(false);

  useEffect(() => {
    let alive = true;
    const ctxAgentId = sessionStorage.getItem("SelectAgentId") || null;
    fetchSetupScore(ctxAgentId).then((s) => {
      if (alive && s) setScore(s);
    });
    return () => {
      alive = false;
    };
  }, []);

  // ── Read agent/business context from the same sessionStorage cache
  // every other onboarding step writes to. Any missing piece falls
  // back to a friendly default rather than blanking the row. ─────────
  const ctx = (() => {
    try {
      const raw = sessionStorage.getItem("agentDetails");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })();
  const agent = ctx?.agent || {};
  const business = ctx?.business || {};

  const agentName = agent.agentName || "Sophia";
  const businessName = business.businessName || "Your business";

  const voipNumber = VOIP_PARSE(agent.voip_numbers);
  const phoneDisplay = formatPhoneDisplay(voipNumber);
  const phoneTel = dialHref(voipNumber);
  const hasNumber = Boolean(voipNumber);

  // Calendar status — one /calender-check on mount. We only need the
  // boolean to decide between "Connected - Google Calendar" and the
  // "Not connected" subtext (still ticks, since we don't gate Go Live
  // on calendar — it's an at-best feature).
  const [calendarConnected, setCalendarConnected] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const tokenStr = localStorage.getItem("token");
        const userId = decodeToken(tokenStr)?.id;
        if (!userId) return;
        const res = await calenderCheck(userId);
        setCalendarConnected(!!res?.data?.connected);
      } catch (err) {
        // Soft-fail: keep the optimistic "Connected" copy if the check
        // fails. Better than scaring the user on the celebration screen.
        console.warn("[GoLiveCelebration] calendar check failed:", err);
      }
    })();
  }, []);

  // ── Confetti on mount. canvas-confetti renders to its own absolutely
  // positioned canvas (auto-injected on first call), so we don't need
  // to mount a <canvas> ourselves. StrictMode fires this twice in dev;
  // confettiFiredRef gates that.
  useEffect(() => {
    if (confettiFiredRef.current) return;
    confettiFiredRef.current = true;

    const palette = ["#6524EB", "#9333EA", "#FFC700", "#10B981", "#FFFFFF"];

    // Stage 1 — bottom-centre cannon, broad spread.
    confetti({
      particleCount: 80,
      spread: 75,
      startVelocity: 42,
      origin: { x: 0.5, y: 0.75 },
      colors: palette,
      ticks: 200,
    });

    // Stage 2 — left + right side cannons fire 250ms later for a
    // layered "real applause" feel without overwhelming the canvas.
    const t = setTimeout(() => {
      confetti({
        particleCount: 45,
        angle: 60,
        spread: 55,
        startVelocity: 45,
        origin: { x: 0, y: 0.85 },
        colors: palette,
        ticks: 200,
      });
      confetti({
        particleCount: 45,
        angle: 120,
        spread: 55,
        startVelocity: 45,
        origin: { x: 1, y: 0.85 },
        colors: palette,
        ticks: 200,
      });
    }, 250);

    return () => clearTimeout(t);
  }, []);

  const handleDialerTap = () => {
    /*
     * REMOVED: markSetupStep("firstRealCall", ctxAgentId)
     *
     * The old comment here said tapping the dialer counts "even though it's an
     * outbound test call". That rule is overturned: firstRealCall now means an
     * actual inbound phone call, nothing else.
     *
     * The backend credits it from Retell's call_ended webhook
     * (agentController.js ~3957), gated on call_type === "phone_call" AND
     * inbound direction — so an outbound tap correctly no longer scores.
     */

    // RN WebView fallback. Most native shells handle tel: links via
    // shouldOverrideUrlLoading / shouldStartLoadWithRequest, so the
    // anchor's default behaviour usually works. But some hosts (older
    // builds, or ones that suppress unknown schemes) need an explicit
    // bridge message — fire one if the bridge exists, then let the
    // anchor's default `tel:` navigation run on top.
    try {
      if (
        typeof window !== "undefined" &&
        window.ReactNativeWebView &&
        typeof window.ReactNativeWebView.postMessage === "function"
      ) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "OPEN_DIALER", number: phoneTel.replace(/^tel:/, "") }),
        );
      }
    } catch (_e) {
      // Posting to the bridge is best-effort; never block the dial.
    }
  };

  const handleGoToDashboard = () => {
    navigate("/fast-agent-detail", { replace: true });
  };

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        {/* ── Headline ─────────────────────────────────────────── */}
        <h1 className={styles.headline}>
          <span className={styles.headlinePurple}>{agentName}</span> is Live!
        </h1>
        <p className={styles.subhead}>
          <span className={styles.purple}>{businessName}</span> is now open
          for calls <span className={styles.purple}>24/7.</span>
        </p>

        {/* ── Sophia avatar ────────────────────────────────────── */}
        <div className={styles.avatarWrap}>
          <div className={styles.avatarCircle}>
            <img src="/svg/receptionist_profile.png" alt="" />
          </div>
        </div>

        {/* ── Setup Score pill ─────────────────────────────────────
            Clickable XP-style pill showing earned points out of 100.
            Tapping opens the breakdown modal. Hidden until the score
            fetch resolves so we don't flash "0 / 100" briefly. */}
        {score && (
          <button
            type="button"
            className={styles.scorePill}
            onClick={() => setShowScoreModal(true)}
            aria-label={`Setup Score: ${score.percent} of 100 points earned. Tap for breakdown.`}
          >
            <span className={styles.scorePillIcon} aria-hidden="true">
              <StarIcon />
            </span>
            <span className={styles.scorePillStack}>
              <span className={styles.scorePillTotal}>
                {score.percent} / 100 pts
              </span>
              <span className={styles.scorePillLabel}>Setup Score</span>
            </span>
            <span className={styles.scorePillChevron} aria-hidden="true">
              <ChevronRight />
            </span>
          </button>
        )}

        {/* ── Setup Summary card ───────────────────────────────── */}
        <div className={styles.summaryCard}>
          <h2 className={styles.summaryTitle}>Your Setup Summary</h2>

          <div className={styles.summaryList}>
            <SummaryRow
              icon={<PhoneIcon />}
              label="Business Number"
              value={hasNumber ? phoneDisplay : "Pending"}
              valuePurple
            />
            <SummaryRow
              icon={<ReceptionistIcon />}
              label="AI Receptionist"
              value={
                <>
                  <span className={styles.purple}>{agentName}</span>
                  <span className={styles.dimmedDivider}> - </span>
                  <span>General Receptionist</span>
                </>
              }
            />
            <SummaryRow
              icon={<CalendarIcon />}
              label="Calendar"
              value={
                calendarConnected ? (
                  <>
                    <span>Connected </span>
                    <span className={styles.dimmedDivider}>- </span>
                    <span className={styles.purple}>Google Calendar</span>
                  </>
                ) : (
                  <span className={styles.dimmedValue}>Not connected</span>
                )
              }
            />
            <SummaryRow
              icon={<ShieldIcon />}
              label="Spam Protection"
              value="Active"
              valuePurple
            />
            <SummaryRow
              icon={<HeadphonesIcon />}
              label="Availability"
              value="24/7"
              valuePurple
            />
          </div>
        </div>

        {/* ── Test-call CTA box ────────────────────────────────── */}
        {hasNumber && (
          <div className={styles.testCallBox}>
            <p className={styles.testCallPrompt}>
              Make a test call to your number now:
            </p>
            <a
              href={phoneTel}
              onClick={handleDialerTap}
              className={styles.dialBtn}
              aria-label={`Dial ${phoneDisplay}`}
            >
              <span className={styles.dialIcon}>
                <PhoneIcon />
              </span>
              <span className={styles.dialNumber}>{phoneDisplay}</span>
              <span className={styles.dialChevron}>
                <ChevronRight />
              </span>
            </a>
          </div>
        )}

        {/* ── Sticky Footer ────────────────────────────────────── */}
        <div className={styles.stickyFooter}>
          <button
            type="button"
            className={styles.dashboardBtn}
            onClick={handleGoToDashboard}
          >
            Go to Dashboard <span aria-hidden="true">→</span>
          </button>

          <ul className={styles.footerList}>
            <li>Your calls are now protected by {agentName}</li>
            <li>We'll notify you on Day 19 before your trial ends.</li>
          </ul>
        </div>
      </div>

      {/* Score breakdown bottom-sheet — opens when the user taps the
          Setup Score pill above. Mounted at the page root (outside
          .inner) so its overlay covers the full viewport regardless
          of the inner container's max-width. */}
      <ScoreBreakdownModal
        isOpen={showScoreModal}
        onClose={() => setShowScoreModal(false)}
        score={score}
      />
    </div>
  );
}

// ── Single Setup Summary row. Extracted because all 5 rows share the
// identical icon-circle + tick badge + label/value layout — keeping it
// inline would force the same nested structure five times. ────────────
function SummaryRow({ icon, label, value, valuePurple = false }) {
  return (
    <div className={styles.summaryRow}>
      <div className={styles.iconWrap}>
        <div className={styles.iconCircle}>{icon}</div>
        <CheckBadge />
      </div>
      <div className={styles.summaryText}>
        <div className={styles.summaryLabel}>{label}</div>
        <div
          className={`${styles.summaryValue} ${valuePurple ? styles.purple : ""}`}
        >
          {value}
        </div>
      </div>
    </div>
  );
}
